import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import type { SalesDocumentRevision } from '@aeris/contracts'
import { formatMoney } from '@aeris/money'
import { parseAmount, ZERO_AMOUNT } from '@aeris/sales'
import { QueryState } from '@aeris/ui'

import { fetchSalesDocumentHistory } from '../lib/api.ts'
import { formatDate } from '../lib/date-format.ts'
import { STATUS_LABELS } from '../lib/sales-labels.ts'

/**
 * Quem mudou o quê num orçamento, pedido ou venda, e quando (F147).
 *
 * O pedido do dono foi este: "todas as alterações vão ficar salvas, quem fez,
 * dia e hora". O servidor grava cada mudança na mesma transação que a faz;
 * aqui só se lê, do mais novo para o mais antigo — a pergunta de quem abre é
 * quase sempre "o que mexeram por último?".
 */

const TIME = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
})

const HEADER_LABELS: Readonly<Record<string, string>> = {
  customer: 'Cliente',
  salesperson: 'Vendedor',
  courier: 'Motoboy',
  notes: 'Observação',
  validUntil: 'Válido até',
  discount: 'Desconto',
  priceList: 'Tabela de preço',
  origin: 'Origem',
}

const money = (value: string | null): string =>
  value === null ? '—' : formatMoney(parseAmount(value) ?? ZERO_AMOUNT)

/** "2.000" vira "2": quantidade inteira não precisa das casas do estoque. */
const quantity = (value: string): string =>
  value.includes('.') ? value.replace(/\.?0+$/, '').replace('.', ',') : value

const headerValue = (key: string, value: string | null): string => {
  if (value === null || value.length === 0) return '—'
  if (key === 'validUntil') return formatDate(value)
  if (key === 'discount') return money(value)
  if (key === 'priceList') return 'outra tabela'
  return value
}

const title = (revision: SalesDocumentRevision): string => {
  switch (revision.action) {
    case 'created':
      return 'Criou'
    case 'edited':
      return 'Alterou'
    case 'dispatched':
      return 'Saiu para entrega'
    case 'cancelled':
      return 'Cancelou'
    case 'transitioned':
      return revision.statusFrom === null || revision.statusTo === null
        ? 'Mudou a situação'
        : `${capitalise(STATUS_LABELS[revision.statusFrom])} → ${STATUS_LABELS[revision.statusTo]}`
  }
}

const capitalise = (text: string): string =>
  text.length === 0 ? text : `${text[0]?.toUpperCase() ?? ''}${text.slice(1)}`

const RevisionBody = ({
  revision,
}: {
  revision: SalesDocumentRevision
}): ReactNode => {
  const details: string[] = []

  for (const [key, change] of Object.entries(revision.header)) {
    details.push(
      `${HEADER_LABELS[key] ?? key}: ${headerValue(key, change.from)} → ${headerValue(key, change.to)}`
    )
  }
  for (const line of revision.lines.changed) {
    const parts: string[] = []
    if (line.quantityFrom !== line.quantityTo) {
      parts.push(
        `${quantity(line.quantityFrom)} → ${quantity(line.quantityTo)}`
      )
    }
    if (line.unitPriceFrom !== line.unitPriceTo) {
      parts.push(
        `preço ${money(line.unitPriceFrom)} → ${money(line.unitPriceTo)}`
      )
    }
    details.push(`${line.description}: ${parts.join(', ')}`)
  }
  for (const line of revision.lines.added) {
    details.push(
      `+ ${line.description} (${quantity(line.quantity)} × ${money(line.unitPrice)})`
    )
  }
  for (const line of revision.lines.removed) {
    details.push(
      `− ${line.description} (${quantity(line.quantity)} × ${money(line.unitPrice)})`
    )
  }
  if (
    revision.action === 'edited' &&
    revision.totalBefore !== null &&
    revision.totalAfter !== null &&
    revision.totalBefore !== revision.totalAfter
  ) {
    details.push(
      `Total: ${money(revision.totalBefore)} → ${money(revision.totalAfter)}`
    )
  }

  return (
    <>
      {details.length === 0 ? null : (
        <ul className="mt-1 flex flex-col gap-0.5 text-(length:--text-sm) text-(--text-secondary)">
          {details.map((detail, index) => (
            <li key={index}>{detail}</li>
          ))}
        </ul>
      )}
      {revision.reason === null ? null : (
        <p className="mt-1 text-(length:--text-sm) text-(--text-muted)">
          Motivo: {revision.reason}
        </p>
      )}
    </>
  )
}

export const SalesDocumentHistory = ({
  documentId,
}: {
  documentId: string
}): ReactNode => {
  const history = useQuery({
    queryKey: ['sales-document-history', documentId],
    queryFn: ({ signal }) => fetchSalesDocumentHistory(documentId, signal),
  })

  const revisions = [...(history.data?.revisions ?? [])].reverse()
  const hasCreation = revisions.some(
    (revision) => revision.action === 'created'
  )

  return (
    <QueryState
      isPending={history.isPending}
      isError={history.isError}
      error={history.error}
      onRetry={() => {
        void history.refetch()
      }}
      what="o histórico"
      isEmpty={revisions.length === 0}
      empty={
        <p className="text-(length:--text-sm) text-(--text-muted)">
          Nenhuma alteração registrada. O histórico começou a ser gravado em
          15/09/2026.
        </p>
      }
    >
      <ol aria-label="Histórico do documento" className="flex flex-col gap-3">
        {revisions.map((revision) => (
          <li
            key={revision.id}
            className="border-l-2 border-(--border-subtle) pl-3"
          >
            <p className="text-(length:--text-sm) text-(--text-primary)">
              <span className="font-medium">{title(revision)}</span>
              <span className="text-(--text-muted)">
                {' · '}
                {revision.actorName ?? 'Sistema'}
                {' · '}
                {formatDate(revision.occurredAt)}{' '}
                {TIME.format(new Date(revision.occurredAt))}
              </span>
            </p>
            <RevisionBody revision={revision} />
          </li>
        ))}
      </ol>
      {hasCreation ? null : (
        <p className="mt-3 text-(length:--text-xs) text-(--text-muted)">
          Documento anterior a 15/09/2026: o que aconteceu antes dessa data não
          foi gravado.
        </p>
      )}
    </QueryState>
  )
}
